import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { hexToRgb } from '@casa/util';
import { drawElements } from '../../elements/layout_elements.ts';
import { CanvasView } from '../../view/canvas_view.tsx';
import { useLegacyIcon } from '../../view/icons.ts';
import { stopEvent } from '../../view/picker_card.tsx';
import { useTapCommand } from '../../view/tap_command.ts';
import { TILE_FONT_FAMILY } from '../../view/theme.ts';
import { useTileEnvironment } from '../../view/tile_environment.ts';
import { LightCard } from './light_card.tsx';
import { CHIP_BOX, changedReadings, hasControls, levelChipOf, lightCommand, lightState, lightTileElements, type LevelChip, type LightChange, type LightTile } from './light_tile.ts';

/** How long a press is held to open the card (LT-9). */
const HOLD_MS = 500;
/** A press that moves further is a scroll, not a hold. */
const HOLD_SLOP = 10;

/** The chip's tint: the light's colour, faint behind the text and stronger around it. */
function tintOf(color: string, alpha: number) : string {
    const rgb = hexToRgb(color);

    return rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})` : `rgba(255, 255, 255, ${alpha})`;
}

/**
 * The level chip (LT-10): in the DOM over the canvas, under the icon, its size from the tile's
 * (container units). A tap opens the card; it neither starts a hold nor switches the light.
 */
function LevelChipButton({ chip, onPress }: { chip: LevelChip; onPress() : void }) : ReactNode {
    return (
        <button
            type="button"
            aria-label={`Brightness and colour: ${chip.text}`}
            title="Brightness and colour"
            onPointerDown={stopEvent}
            onContextMenu={(event) => event.preventDefault()}
            onClick={(event) => {
                stopEvent(event);
                onPress();
            }}
            style={{
                // Sized by its text, centred in its box, so that it keeps its shape on a narrow tile.
                position: 'absolute',
                left: '50%',
                top: `${(CHIP_BOX.top + CHIP_BOX.height / 2) * 100}%`,
                maxWidth: '90%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3em',
                padding: '0.35em 0.9em',
                border: `1px solid ${chip.tint ? tintOf(chip.tint, 0.55) : 'rgba(255, 255, 255, 0.14)'}`,
                borderRadius: 999,
                background: chip.tint ? tintOf(chip.tint, 0.22) : 'rgba(255, 255, 255, 0.06)',
                color: chip.tint ? '#f2f2f2' : '#a0a0a0',
                fontFamily: TILE_FONT_FAMILY,
                fontSize: 'min(10cqh, 11cqw)',
                fontWeight: 600,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                touchAction: 'manipulation',
            }}
        >
            {chip.text}
            <span aria-hidden="true" style={{ opacity: 0.7 }}>›</span>
        </button>
    );
}

/**
 * The content area of a `light` tile (docs/specs/light-tile.md): its icon in the light's colour and
 * its level chip; a tap switches the light, a hold or a tap on the chip opens its card.
 */
export function LightTileView({ tile }: { tile: LightTile }) : ReactNode {
    const { source } = tile.options;
    const environment = useTileEnvironment();
    const readings = environment.useReadings(tile, source);
    const tap = useTapCommand(tile, source);
    const send = environment.useCommand(tile, source);
    const icon = useLegacyIcon(tile.options.icon);
    const state = lightState(readings);
    const canOpen = hasControls(state);
    const chip = levelChipOf(state);
    const area = useRef<HTMLDivElement>(null);
    const [anchor, setAnchor] = useState<DOMRect | null>(null);

    // A hold: a timer from the press, cancelled by letting go or moving; when it fires, the click
    // that ends the press does not switch the light.
    const hold = useRef<{ timer: ReturnType<typeof setTimeout>; x: number; y: number } | undefined>(undefined);
    const held = useRef(false);
    const cancelHold = () : void => {
        if (hold.current) {
            clearTimeout(hold.current.timer);
            hold.current = undefined;
        }
    };
    useEffect(() => cancelHold, []);

    const open = () : void => setAnchor(area.current?.closest('[data-tile]')?.getBoundingClientRect() ?? area.current?.getBoundingClientRect() ?? null);
    const change = (next: LightChange) : void => {
        const { command, argument } = lightCommand(next);
        send(command, argument, changedReadings(readings, next)).catch((error: unknown) => {
            // The tile shows the failure with its warning mark; this is for the console.
            console.warn(`${tile.label ?? tile.type}: ${command}: ${error instanceof Error ? error.message : String(error)}`);
        });
    };
    const toggle = () : void => {
        if (held.current) {
            held.current = false;
            return;
        }
        const next = state.on !== true;
        void tap(next ? 'on' : 'off', undefined, changedReadings(readings, { on: next }));
    };

    return (
        <div
            ref={area}
            // A size container: the chip's text follows the tile's size.
            style={{ position: 'absolute', inset: 0, containerType: 'size', cursor: 'pointer', touchAction: 'manipulation', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' } as CSSProperties}
            onPointerDown={(event) => {
                held.current = false;
                cancelHold();
                if (canOpen) {
                    hold.current = {
                        x: event.clientX,
                        y: event.clientY,
                        timer: setTimeout(() => {
                            hold.current = undefined;
                            held.current = true;
                            open();
                        }, HOLD_MS),
                    };
                }
            }}
            onPointerMove={(event) => {
                if (hold.current && Math.hypot(event.clientX - hold.current.x, event.clientY - hold.current.y) > HOLD_SLOP) {
                    cancelHold();
                }
            }}
            onPointerUp={cancelHold}
            onPointerCancel={cancelHold}
            onPointerLeave={cancelHold}
            onContextMenu={(event) => event.preventDefault()}
            onClick={toggle}
        >
            <CanvasView deps={[JSON.stringify(state), icon]} draw={(ctx, width, height) => drawElements(ctx, { left: 0, top: 0, width, height }, lightTileElements(state, icon))} />
            {chip ? <LevelChipButton chip={chip} onPress={open} /> : null}
            {anchor && <LightCard anchor={anchor} title={tile.label ?? 'Light'} state={state} onChange={change} onClose={() => setAnchor(null)} />}
        </div>
    );
}
